import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertTriangle, Settings, TestTube } from 'lucide-react';
import { validateCloudinaryConfig, logConfigurationStatus } from '@/lib/cloudinary-config-parser';

export function CloudinaryConfigTest() {
  const config = validateCloudinaryConfig();
  
  // Get environment variables for display (without exposing secrets)
  const envVars = {
    VITE_CLOUDINARY_URL: import.meta.env.VITE_CLOUDINARY_URL,
    VITE_CLOUDINARY_CLOUD_NAME: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME,
    VITE_CLOUDINARY_API_KEY: import.meta.env.VITE_CLOUDINARY_API_KEY,
    VITE_CLOUDINARY_UPLOAD_PRESET: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET,
  };

  // Parse URL to show derived values
  let parsedFromUrl = { cloudName: '', apiKey: '', hasApiSecret: false };
  if (envVars.VITE_CLOUDINARY_URL) {
    try {
      const urlWithoutProtocol = envVars.VITE_CLOUDINARY_URL.replace('cloudinary://', '');
      const [credentials, cloudName] = urlWithoutProtocol.split('@');
      const [apiKey, apiSecret] = credentials.split(':');
      parsedFromUrl = {
        cloudName: cloudName || '',
        apiKey: apiKey || '',
        hasApiSecret: !!apiSecret
      };
    } catch (error) {
      console.warn('Error parsing CLOUDINARY_URL:', error);
    }
  }

  const maskValue = (value: string | undefined, key: string) => {
    if (!value || value === 'your_cloud_name_here' || value === 'your_api_key_here' || value === 'your_upload_preset_here') {
      return '❌ Not set';
    }
    
    // Special handling for CLOUDINARY_URL
    if (key === 'VITE_CLOUDINARY_URL') {
      if (value.startsWith('cloudinary://')) {
        const parts = value.replace('cloudinary://', '').split('@');
        if (parts.length === 2) {
          const [credentials, cloudName] = parts;
          const [apiKey] = credentials.split(':');
          return `cloudinary://${apiKey.substring(0, 6)}...@${cloudName}`;
        }
      }
    }
    
    return value.length > 10 ? `${value.substring(0, 6)}...${value.substring(value.length - 4)}` : value;
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Cloudinary Configuration Test
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Overall Status */}
          <div>
            {config.isValid ? (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  ✅ Cloudinary configuration is valid and ready for uploads
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  ❌ Cloudinary configuration has issues that need to be resolved
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Environment Variables Status */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Environment Variables</h3>
            
            {Object.entries(envVars).map(([key, value]) => {
              let isSet = value && value !== `your_${key.split('_').pop()?.toLowerCase()}_here`;
              let displayValue = value;
              
              // Show parsed values for individual variables when using URL format
              if (!value && envVars.VITE_CLOUDINARY_URL) {
                if (key === 'VITE_CLOUDINARY_CLOUD_NAME' && parsedFromUrl.cloudName) {
                  isSet = true;
                  displayValue = parsedFromUrl.cloudName;
                } else if (key === 'VITE_CLOUDINARY_API_KEY' && parsedFromUrl.apiKey) {
                  isSet = true;
                  displayValue = parsedFromUrl.apiKey;
                }
              }
              
              return (
                <div key={key} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center gap-2">
                    {isSet ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className="font-mono text-sm">{key}</span>
                    {!value && displayValue && (
                      <span className="text-xs text-blue-600">(from URL)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={isSet ? 'default' : 'destructive'}>
                      {isSet ? 'Set' : 'Missing'}
                    </Badge>
                    <span className="text-sm text-muted-foreground font-mono">
                      {maskValue(displayValue, key)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Configuration Errors */}
          {config.errors.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-red-600">Configuration Errors</h3>
              <div className="space-y-2">
                {config.errors.map((error, index) => (
                  <Alert key={index} variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ))}
              </div>
            </div>
          )}

          {/* Setup Instructions */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Setup Instructions</h3>
            <div className="bg-gray-50 p-4 rounded text-sm space-y-2">
              <p><strong>1. Create a .env file</strong> in your project root if it doesn't exist</p>
              <p><strong>2. Add your Cloudinary credentials (choose one format):</strong></p>
              
              <p className="text-sm font-medium">Option A: CLOUDINARY_URL format (recommended)</p>
              <pre className="bg-white p-2 rounded border text-xs overflow-x-auto mb-2">
{`VITE_CLOUDINARY_URL=cloudinary://your_api_key:your_api_secret@your_cloud_name
VITE_CLOUDINARY_UPLOAD_PRESET=your_upload_preset`}
              </pre>
              <p className="text-xs text-blue-600 mb-2">
                ℹ️ When using URL format, individual variables will show as "(from URL)" above
              </p>
              
              <p className="text-sm font-medium">Option B: Individual variables</p>
              <pre className="bg-white p-2 rounded border text-xs overflow-x-auto">
{`VITE_CLOUDINARY_CLOUD_NAME=your_actual_cloud_name
VITE_CLOUDINARY_API_KEY=your_actual_api_key
VITE_CLOUDINARY_UPLOAD_PRESET=your_actual_upload_preset`}
              </pre>
              <p><strong>3. Get your credentials from:</strong></p>
              <ul className="ml-4 list-disc space-y-1">
                <li>Cloud Name: Cloudinary Dashboard header</li>
                <li>API Key: Dashboard → Account Details</li>
                <li>Upload Preset: Settings → Upload → Create unsigned preset</li>
              </ul>
              <p><strong>4. Restart your development server</strong> after updating .env</p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={() => {
                  logConfigurationStatus();
                  console.log('🔍 Check the browser console for detailed configuration status');
                }}
                variant="outline"
                size="sm"
              >
                <TestTube className="h-4 w-4 mr-2" />
                Test Configuration (Check Console)
              </Button>
              <Button 
                onClick={() => window.location.reload()}
                variant="outline"
                size="sm"
              >
                Refresh Page
              </Button>
            </div>
          </div>

          {/* Test Upload URL */}
          {config.isValid && (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-green-600">Ready to Test</h3>
              <p className="text-sm text-muted-foreground">
                Your configuration looks good! You can now test uploads at:
              </p>
              <div className="bg-green-50 p-2 rounded">
                <a 
                  href="/cloudinary-upload-demo" 
                  className="text-blue-600 hover:underline font-mono text-sm"
                >
                  /cloudinary-upload-demo
                </a>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 